// lazyWithRetry — drop-in replacement for React.lazy that handles the
// "stale chunk after deploy" failure mode.
//
// Failure mode it fixes:
//   1. User loads the app; React caches a module map referring to chunk
//      filenames like /assets/Settings-AbC12345.js.
//   2. We deploy. Vite emits new hashed filenames. Old files are gone.
//   3. User clicks a route they haven't visited yet. React.lazy() fires
//      `import("…Settings-AbC12345.js")` which 404s. The Promise rejects;
//      React renders nothing → blank screen.
//   4. A full page refresh re-fetches index.html with the NEW chunk
//      names and the page works again.
//
// What we do:
//   - First failure: retry the import once after a small delay
//     (handles transient network blips).
//   - Second failure: force a hard reload — that always pulls fresh
//     index.html via the service worker's network-first navigation
//     path, which fixes the underlying chunk-name mismatch.
//   - Use sessionStorage as a one-shot guard so a TRULY missing chunk
//     (real bug, not stale deploy) doesn't reload-loop the user out of
//     a working session.

import { lazy, type ComponentType, type LazyExoticComponent } from "react";

const RELOAD_FLAG = "fk_chunk_reload_at";
const RELOAD_DEBOUNCE_MS = 30_000;

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const msg = err instanceof Error ? err.message : String(err);
  const name = err instanceof Error ? err.name : "";
  // Covers the strings Vite/webpack/Safari/Firefox throw on stale chunks.
  return (
    name === "ChunkLoadError" ||
    /Loading chunk/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

function reloadOnce(): never {
  // Guard so we don't infinite-loop if the chunk is genuinely missing.
  const last = Number(sessionStorage.getItem(RELOAD_FLAG) || "0");
  const now = Date.now();
  if (now - last < RELOAD_DEBOUNCE_MS) {
    // Already tried recently — bubble up so the error boundary can
    // surface a real error UI instead of looping.
    throw new Error(
      "FieldKrew is having trouble loading this page after an update. Please try refreshing.",
    );
  }
  sessionStorage.setItem(RELOAD_FLAG, String(now));
  // Hard reload — bypasses the SW's app-shell cache and pulls fresh HTML
  // which has the current chunk filenames baked in.
  window.location.reload();
  // window.location.reload() is async-ish; we still need to "return"
  // something to the lazy() caller. Throwing keeps the Suspense fence in
  // a pending state until the reload completes.
  throw new Error("Reloading for new app version…");
}

export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await factory();
      // Successful load — clear the guard so future stale-deploy events
      // can trigger a reload again.
      sessionStorage.removeItem(RELOAD_FLAG);
      return mod;
    } catch (err) {
      if (!isChunkLoadError(err)) throw err;
      // One retry first — most transient failures resolve here.
      try {
        await new Promise((r) => setTimeout(r, 400));
        const mod = await factory();
        sessionStorage.removeItem(RELOAD_FLAG);
        return mod;
      } catch (retryErr) {
        if (!isChunkLoadError(retryErr)) throw retryErr;
        reloadOnce();
      }
    }
  });
}
